import { http } from "@/lib/http";

import type {
  AccountSummary,
  BillingRateTable,
  Budget,
  BudgetCreate,
  CostEstimateResponse,
  Invoice,
  PlannedResource,
  SampleUsageResponse,
  UsageExportQuery,
  UsageQuery,
  UsageSummary,
} from "./types";

export async function getAccount(): Promise<AccountSummary> {
  const { data } = await http.get<AccountSummary>("/billing/account");
  return data;
}

export async function listInvoices(projectId?: string): Promise<Invoice[]> {
  // project_id lets an admin scope to another tenant; the backend ignores it
  // for non-admins (members are always scoped to their own project).
  const { data } = await http.get<Invoice[]>("/billing/invoices", {
    params: projectId ? { project_id: projectId } : {},
  });
  return data;
}

export async function generateInvoice(body: {
  period_start: string;
  period_end: string;
}): Promise<Invoice> {
  const { data } = await http.post<Invoice>("/billing/invoices", body);
  return data;
}

export async function payInvoice(invoiceId: string): Promise<Invoice> {
  const { data } = await http.post<Invoice>(
    `/billing/invoices/${invoiceId}/pay`,
    {},
  );
  return data;
}

export async function getUsage(params: UsageQuery): Promise<UsageSummary> {
  const { data } = await http.get<UsageSummary>("/billing/usage", { params });
  return data;
}

export async function estimateCost(
  resources: PlannedResource[],
): Promise<CostEstimateResponse> {
  const { data } = await http.post<CostEstimateResponse>("/billing/estimate", {
    resources,
  });
  return data;
}

export async function getRates(): Promise<BillingRateTable> {
  const { data } = await http.get<BillingRateTable>("/billing/rates");
  return data;
}

export async function triggerSample(): Promise<SampleUsageResponse> {
  const { data } = await http.post<SampleUsageResponse>("/billing/sample");
  return data;
}

// --- admin account actions (operate on another user's account) --- //
export async function addCredit(
  userId: string,
  amount: number,
): Promise<void> {
  await http.post(`/billing/account/${userId}/credit`, { amount });
}

export async function suspendAccount(userId: string): Promise<void> {
  await http.post(`/billing/account/${userId}/suspend`);
}

export async function reactivateAccount(userId: string): Promise<void> {
  await http.post(`/billing/account/${userId}/reactivate`);
}

// --- usage export, budgets (metering plan Item 7) ------------------------ //
/** The usage rows of a window as a CSV file (the browser saves it). */
export async function exportUsage(
  params: UsageExportQuery,
): Promise<{ blob: Blob; filename: string }> {
  const response = await http.get("/billing/usage/export", {
    params: { ...params, format: "csv" },
    responseType: "blob",
  });
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: response.data as Blob, filename: match?.[1] ?? "usage.csv" };
}

export async function setUsageExportDelivery(
  enabled: boolean,
): Promise<AccountSummary> {
  const { data } = await http.put<AccountSummary>(
    "/billing/account/usage-export",
    { enabled },
  );
  return data;
}

export async function listBudgets(): Promise<Budget[]> {
  const { data } = await http.get<Budget[]>("/billing/budgets");
  return data;
}

export async function createBudget(body: BudgetCreate): Promise<Budget> {
  const { data } = await http.post<Budget>("/billing/budgets", body);
  return data;
}

export async function deleteBudget(budgetId: string): Promise<void> {
  await http.delete(`/billing/budgets/${budgetId}`);
}
